import React, {useState} from "react";
import {createPortal} from "react-dom";
import {keepPreviousData, useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import toast from "react-hot-toast";
import {MapPin, Phone, ReceiptText} from "lucide-react";
import Button from "../Common/Button";
import {
    ListCard, ListEmpty, ListLoader, Modal, ModalFooter, Pagination, ToolbarSelect, mobileListClass, mobileRowClass, thClass,
} from "../../stock/StockUI";
import {
    ORDER_STATUS_CLASSES,
    ORDER_STATUS_LABELS,
    apiErrorMessage,
    cancelMarketplaceOrder,
    fetchMarketplaceOrders,
    formatDateTime,
    formatMoney,
} from "./marketplaceApi";

const PAGE_SIZE = 20;

const StatusBadge = ({status}) => (
    <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${ORDER_STATUS_CLASSES[status] || ORDER_STATUS_CLASSES.cancelled}`}>
        {ORDER_STATUS_LABELS[status] || status}
    </span>
);

const itemsSummary = (items = []) =>
    items.map((i) => `${i.quantity} × ${i.name}`).join(" · ");

// ─── Détail ──────────────────────────────────────────────────────────────────

const OrderDetailModal = ({order, onClose}) => {
    const queryClient = useQueryClient();
    const [confirming, setConfirming] = useState(false);

    const cancel = useMutation({
        mutationFn: () => cancelMarketplaceOrder(order.id),
        onSuccess: () => {
            toast.success(`Commande ${order.reference} annulée`);
            queryClient.invalidateQueries({queryKey: ["marketplace", "orders"]});
            onClose();
        },
        onError: (error) => toast.error(apiErrorMessage(error, "Impossible d'annuler la commande")),
    });

    const footer = order.cancellable ? (
        confirming ? (
            <ModalFooter onCancel={() => setConfirming(false)} onConfirm={() => cancel.mutate()}
                         label="Confirmer l'annulation" saving={cancel.isPending} tone="text-red-600 dark:text-red-400"/>
        ) : (
            <div className="border-t border-gray-200 dark:border-gray-700">
                <button type="button" onClick={() => setConfirming(true)}
                        className="w-full py-4 font-medium text-red-600 dark:text-red-400 hover:bg-gray-50 dark:hover:bg-gray-700">
                    Annuler la commande
                </button>
            </div>
        )
    ) : null;

    return createPortal(
        <Modal title={`Commande ${order.reference}`} subtitle={formatDateTime(order.created_at)} onClose={onClose} size="lg" footer={footer}>
            <div className="space-y-5">
                <div className="flex items-center justify-between gap-3">
                    <StatusBadge status={order.status}/>
                    {order.ordered_by && <span className="text-xs text-gray-500 dark:text-gray-400 truncate">par {order.ordered_by}</span>}
                </div>

                <div className="rounded-lg border border-gray-200 dark:border-gray-700 divide-y divide-gray-100 dark:divide-gray-700">
                    {order.items.map((item) => (
                        <div key={item.product_id} className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
                            <span className="text-gray-900 dark:text-gray-100">{item.quantity} × {item.name}</span>
                            <span className="font-medium text-gray-900 dark:text-gray-100 whitespace-nowrap">
                                {formatMoney(item.unit_price * item.quantity)}
                            </span>
                        </div>
                    ))}
                    <div className="px-3 py-2 space-y-1 text-sm">
                        <div className="flex justify-between text-gray-600 dark:text-gray-300">
                            <span>Sous-total</span><span>{formatMoney(order.subtotal)}</span>
                        </div>
                        {order.discount > 0 && (
                            <div className="flex justify-between text-red-600 dark:text-red-400">
                                <span>Remises</span><span>-{formatMoney(order.discount)}</span>
                            </div>
                        )}
                        {order.shipping_fee > 0 && (
                            <div className="flex justify-between text-gray-600 dark:text-gray-300">
                                <span>Livraison</span><span>{formatMoney(order.shipping_fee)}</span>
                            </div>
                        )}
                        <div className="flex justify-between pt-1 font-semibold text-gray-900 dark:text-gray-100">
                            <span>Total TTC</span><span>{formatMoney(order.total)}</span>
                        </div>
                    </div>
                </div>

                <div className="rounded-lg bg-gray-50 dark:bg-gray-700/40 p-3 space-y-2 text-sm text-gray-700 dark:text-gray-300">
                    <div className="font-medium text-gray-900 dark:text-gray-100">{order.contact_name}</div>
                    <div className="flex items-center gap-2"><Phone className="w-4 h-4 text-primary"/> {order.phone}</div>
                    <div className="flex items-start gap-2 whitespace-pre-line"><MapPin className="w-4 h-4 mt-0.5 text-primary flex-shrink-0"/> {order.address}</div>
                    {order.note && <div className="text-gray-500 dark:text-gray-400 whitespace-pre-line">Remarque : {order.note}</div>}
                </div>
            </div>
        </Modal>,
        document.body
    );
};

// ─── Liste ───────────────────────────────────────────────────────────────────

const MarketplaceOrdersTab = ({onBrowse}) => {
    const [status, setStatus] = useState("");
    const [page, setPage] = useState(1);
    const [selected, setSelected] = useState(null);

    const {data, isLoading, isError} = useQuery({
        queryKey: ["marketplace", "orders", status, page],
        queryFn: () => fetchMarketplaceOrders({status, page, itemsPerPage: PAGE_SIZE}),
        placeholderData: keepPreviousData,
    });

    const orders = data?.items || [];
    const total = data?.total || 0;
    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

    return (
        <div className="max-w-full">
            <div className="flex flex-col sm:flex-row">
                <ToolbarSelect label="Statut" value={status} className="sm:w-64"
                               onChange={(value) => { setStatus(value); setPage(1); }}>
                    <option value="">Tous les statuts</option>
                    {Object.entries(ORDER_STATUS_LABELS).map(([key, label]) => <option key={key} value={key}>{label}</option>)}
                </ToolbarSelect>
            </div>

            {isLoading ? (
                <ListLoader/>
            ) : isError ? (
                <p className="text-center text-red-500 py-12">Impossible de charger vos commandes.</p>
            ) : orders.length === 0 ? (
                <ListEmpty
                    icon={ReceiptText}
                    title={status ? "Aucune commande avec ce statut" : "Aucune commande pour le moment"}
                    text="Vos commandes de matériel apparaîtront ici avec leur suivi."
                    action={!status && <Button variant="primary" className="mt-4" onClick={onBrowse}>Voir le catalogue</Button>}
                />
            ) : (
                <ListCard>
                    {/* Desktop table */}
                    <div className="hidden lg:block overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                            <thead className="bg-gray-50 dark:bg-gray-700/50">
                            <tr>
                                <th className={thClass}>Commande</th>
                                <th className={thClass}>Articles</th>
                                <th className={thClass}>Livraison</th>
                                <th className={`${thClass} text-right`}>Total</th>
                                <th className={thClass}>Statut</th>
                            </tr>
                            </thead>
                            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                            {orders.map((order) => (
                                <tr key={order.id} onClick={() => setSelected(order)}
                                    className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors cursor-pointer">
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="text-sm font-semibold text-gray-900 dark:text-gray-100 font-mono">{order.reference}</div>
                                        <div className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">{formatDateTime(order.created_at)}</div>
                                    </td>
                                    <td className="px-6 py-4 text-sm text-gray-700 dark:text-gray-300">
                                        <div className="line-clamp-2 max-w-md">{itemsSummary(order.items)}</div>
                                    </td>
                                    <td className="px-6 py-4 text-sm text-gray-700 dark:text-gray-300">
                                        <div>{order.contact_name}</div>
                                        <div className="text-xs text-gray-500 dark:text-gray-400">{order.phone}</div>
                                    </td>
                                    <td className="px-6 py-4 text-right text-sm font-semibold text-gray-900 dark:text-gray-100 whitespace-nowrap">
                                        {formatMoney(order.total)}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap"><StatusBadge status={order.status}/></td>
                                </tr>
                            ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Mobile rows */}
                    <div className={mobileListClass}>
                        {orders.map((order) => (
                            <div key={order.id} onClick={() => setSelected(order)} className={`${mobileRowClass} cursor-pointer`}>
                                <div className="flex items-start justify-between gap-3">
                                    <div>
                                        <div className="text-sm font-semibold text-gray-900 dark:text-gray-100 font-mono">{order.reference}</div>
                                        <div className="text-xs text-gray-500 dark:text-gray-400">{formatDateTime(order.created_at)}</div>
                                    </div>
                                    <StatusBadge status={order.status}/>
                                </div>
                                <div className="mt-2 text-xs text-gray-600 dark:text-gray-300 line-clamp-2">{itemsSummary(order.items)}</div>
                                <div className="mt-2 text-sm font-semibold text-gray-900 dark:text-gray-100">{formatMoney(order.total)}</div>
                            </div>
                        ))}
                    </div>

                    <Pagination page={page} totalPages={totalPages} onPage={setPage}
                                summary={`${total} commande${total > 1 ? "s" : ""}`}/>
                </ListCard>
            )}

            {selected && <OrderDetailModal order={selected} onClose={() => setSelected(null)}/>}
        </div>
    );
};

export default MarketplaceOrdersTab;
